import React, {ChangeEvent} from "react";
import {LibraryDomainUseDTO, LibraryDTO, LibraryGoalUseDTO, LibraryIndicatorUseDTO} from "../../../models/library";
import {compareByAttr} from "../../../utils/compare";
import {LibraryDomainBrowser} from "./LibraryDomainBrowser";
import {Box, Button, Container, InputAdornment, TextField, Typography} from "@material-ui/core";
import {createStyles, makeStyles, Theme} from "@material-ui/core/styles";
import {useTranslation} from "react-i18next";
import SearchIcon from "@material-ui/icons/Search";
import TuneIcon from "@material-ui/icons/Tune";
import WorkingSVG from "../../../graphics/undraw/factory_dy0a.svg";
import SearchingSVG from "../../../graphics/undraw/searching_re_stk8.svg";

interface LibraryBrowserProps {
    library: LibraryDTO;
}

interface LibraryBrowserState {
    domains: LibraryDomainUseDTO[];
    filter: string;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        filterLabel: {
            fontSize: "0.8125rem",
            fontWeight: 600,
            color: "#374151",
            marginBottom: theme.spacing(0.75),
        },
        filterRow: {
            display: "flex",
            gap: theme.spacing(1.5),
            alignItems: "center",
            marginBottom: theme.spacing(2.5),
        },
        filterField: {
            flexGrow: 1,
            backgroundColor: "#ffffff",
            borderRadius: "8px",
        },
        filtersButton: {
            flexShrink: 0,
            borderRadius: "8px",
            textTransform: "none" as const,
            fontWeight: 600,
            color: "#374151",
            borderColor: "#E5E7EB",
            backgroundColor: "#ffffff",
            whiteSpace: "nowrap" as const,
        },
    })
);

function matchIndicator(indicator: LibraryIndicatorUseDTO, filter: string): boolean {
    return indicator.indicator.name.toLowerCase().includes(filter);
}

function filterIndicators(indicators: LibraryIndicatorUseDTO[], filter: string): LibraryIndicatorUseDTO[] {
    return indicators
        .filter((indicator) => matchIndicator(indicator, filter))
        .sort(compareByAttr("order", "ascending"));
}

function matchGoal(goal: LibraryGoalUseDTO, filter: string): boolean {
    return goal.goal.name.toLowerCase().includes(filter);
}

function filterGoals(goals: LibraryGoalUseDTO[], filter: string): LibraryGoalUseDTO[] {
    return goals
        .map(
            (goal): LibraryGoalUseDTO => ({
                ...goal,
                libraryIndicatorUses: filterIndicators(goal.libraryIndicatorUses, filter),
            })
        )
        .filter((goal) => goal.libraryIndicatorUses.length > 0 || matchGoal(goal, filter))
        .sort(compareByAttr("order", "ascending"));
}

function matchDomain(domain: LibraryDomainUseDTO, filter: string): boolean {
    return domain.domain.name.toLowerCase().includes(filter);
}

function filterDomains(domains: LibraryDomainUseDTO[], filter: string): LibraryDomainUseDTO[] {
    return domains
        .map(
            (domain): LibraryDomainUseDTO => ({
                ...domain,
                libraryGoalUses: filterGoals(domain.libraryGoalUses, filter),
            })
        )
        .filter((domain) => domain.libraryGoalUses.length > 0 || matchDomain(domain, filter))
        .sort(compareByAttr("order", "ascending"));
}

export const LibraryBrowser: React.FC<LibraryBrowserProps> = (props: LibraryBrowserProps) => {
    const [state, setState] = React.useState<LibraryBrowserState>({
        domains: props.library.libraryDomainUses,
        filter: "",
    });
    const classes = useStyles();
    const [t] = useTranslation("libraries");

    const handleFilterChange = (event: ChangeEvent<HTMLInputElement>) => {
        const filter = event.target.value.toLowerCase();
        setState({
            filter: filter,
            domains: filterDomains(props.library.libraryDomainUses, filter),
        });
    };

    const noDomain = props.library.libraryDomainUses.length === 0;
    const allFiltered = !noDomain && state.domains.length === 0;
    return (
        <Container maxWidth="xl" disableGutters>
            <Typography className={classes.filterLabel}>{t("library.filters.byName")}</Typography>
            <Box className={classes.filterRow}>
                <TextField
                    className={classes.filterField}
                    placeholder={t("library.list.filter")}
                    value={state.filter}
                    onChange={handleFilterChange}
                    variant="outlined"
                    size="small"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon style={{color: "#9CA3AF", fontSize: "1rem"}} />
                            </InputAdornment>
                        ),
                    }}
                />
                <Button
                    variant="outlined"
                    className={classes.filtersButton}
                    startIcon={<TuneIcon style={{fontSize: "1rem"}} />}
                >
                    Filters
                </Button>
            </Box>
            {state.domains.map((domain) => (
                <LibraryDomainBrowser key={domain.id} libraryDomainUse={domain} {...props} />
            ))}
            {noDomain && (
                <Box style={{textAlign: "center"}}>
                    <h2>{t("library.no_domains")}</h2>
                    <img src={WorkingSVG} style={{maxWidth: "40em", margin: "3em"}} alt="no-domains" />
                </Box>
            )}
            {allFiltered && (
                <Box style={{textAlign: "center"}}>
                    <h2>{t("library.no_domains_filtered")}</h2>
                    <img src={SearchingSVG} style={{maxWidth: "40em", margin: "3em"}} alt="no-domains" />
                </Box>
            )}
        </Container>
    );
};
